import { Premise } from "@features/cx/consumerManagement/types";
import { DynamicFormV2 } from "@shared/components/DynamicFormV2";
import { StepHelpers } from "@shared/components/Stepper";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { Button } from "@shared/ui/button";
import {
  AlertCircle,
  Building,
  Check,
  ChevronLeft,
  ChevronRight,
  Home,
  MapPin,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useEditConsumer, usePremise } from "../../../hooks";
import { useParams } from "react-router-dom";
import { toast } from "@shared/hooks/use-toast";

interface AddressDetailsProps {
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

interface AddressData {
  // Service Address
  houseNumber: string;
  streetAddress: string;
  city: string;
  area: string;
  subArea: string;
  premiseName?: any;
  zipCode: string;
  premiseNameDisplay?: string;
  // Billing Address
  sameAsBilling: boolean;
  billingHouseNumber?: string;
  billingStreetAddress?: string;
  billingCity?: string;
  billingArea?: string;
  billingSubArea?: string;
  billingPremiseName?: any;
  billingZipCode?: string;
  billingPremiseNameDisplay?: string;
  // Metadata
  serviceAvailable?: boolean;
  timestamp?: string;
}

export function AddressDetailsStep({
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 1,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating,
}: AddressDetailsProps) {
  const [serviceAvailable, setServiceAvailable] = useState<boolean | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const editConsumerMutation = useEditConsumer();
  const { consumerId } = useParams<{ consumerId: string }>();

  // Get remote utility ID
  const remoteUtilityId = useRemoteUtilityId();

  // Fetch premises using hooks
  const { data: premisesData, isLoading: isLoadingPremises } = usePremise({
    remote_utility_id: remoteUtilityId,
    config_level: "premise",
  });

  // Transform premises to options - memoized to prevent re-renders
  const premiseOptions = useMemo(() => {
    return (
      premisesData?.result?.map((premise: Premise) => ({
        value: premise.code,
        label: premise.name,
        data: premise,
      })) || []
    );
  }, [premisesData]);

  // Helper to get premise by code - memoized
  const getPremiseByCode = useCallback(
    (premiseCode: string): Premise | null => {
      console.log("debug premise sleecet", premiseCode?.[0]);
      if (!premisesData?.result || !premiseCode) return null;
      return (
        premisesData.result.find(
          (premise: Premise) => premise.code === premiseCode?.[0]
        ) || null
      );
    },
    [premisesData]
  );

  // Check service availability - memoized
  const checkServiceAvailability = useCallback(
    async (city: string, area: string, zipCode: string) => {
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const unavailableCombinations = [{ zipCode: "99999" }];
        const isUnavailable = unavailableCombinations.some(
          (combo) => combo.zipCode && combo.zipCode === zipCode
        );

        const available = !isUnavailable;
        setServiceAvailable(available);
        return available;
      } catch (error) {
        console.error("Service availability check failed:", error);
        setServiceAvailable(null);
        return null;
      }
    },
    []
  );

  // Define form fields - memoized to prevent recreation
  const formFields: FormField[] = useMemo(
    () => [
      // Service Address Section
      {
        name: "houseNumber",
        label: "House/Unit Number",
        type: "text",
        required: true,
        placeholder: "e.g. 123, Apt 4B",
        helperText: "Enter your building or unit identifier",
        group: "Service Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-blue-500",
        groupOrder: 1,
      },
      {
        name: "streetAddress",
        label: "Street Address",
        type: "text",
        required: true,
        placeholder: "e.g. 123 Main Street",
        helperText:
          "Enter the full street address without apartment/unit number",
        group: "Service Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-blue-500",
        groupOrder: 2,
      },
      {
        name: "premiseName",
        label: "Premise",
        type: "multi-select",
        maxSelection: 1,
        closeOnSelect: true,
        required: true,
        searchable: true,
        placeholder: isLoadingPremises
          ? "Loading premises..."
          : "Select premise",
        options: premiseOptions,
        helperText: "Select the premise for service installation",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Service Address",
        groupColor: "border-l-blue-500",
        groupOrder: 3,
      },
      {
        name: "city",
        label: "City",
        type: "text",
        required: true,
        placeholder: "City will be auto-filled",
        helperText: "Auto-populated based on premise selection",
        disabled: true,
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Service Address",
        groupColor: "border-l-blue-500",
        groupOrder: 4,
      },
      {
        name: "area",
        label: "Area",
        type: "text",
        required: true,
        placeholder: "Area will be auto-filled",
        helperText: "Auto-populated based on premise selection",
        disabled: true,
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Service Address",
        groupColor: "border-l-blue-500",
        groupOrder: 5,
      },
      {
        name: "subArea",
        label: "Sub Area",
        type: "text",
        required: true,
        placeholder: "Sub area will be auto-filled",
        helperText: "Auto-populated based on premise selection",
        disabled: true,
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Service Address",
        groupColor: "border-l-blue-500",
        groupOrder: 6,
      },
      {
        name: "zipCode",
        label: "Zip/Postal Code",
        type: "text",
        required: false,
        placeholder: "e.g. 12345",
        // pattern: "^[0-9]{5}(-[0-9]{4})?$",
        helperText: "Enter your  postal code",
        group: "Service Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-blue-500",
        groupOrder: 7,
      },
      {
        name: "sameAsBilling",
        label: "Same as service address",
        type: "checkbox",
        required: false,
        helperText:
          "Check this if your billing address is the same as your service address",
        group: "Service Address",
        classes: {
          container: "mb-4 col-span-2",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-blue-500",
        groupOrder: 8,
      },

      // Billing Address Section
      {
        name: "billingHouseNumber",
        label: " House/Unit Number",
        type: "text",
        required: false,
        placeholder: "e.g. 123, Apt 4B",
        helperText: "Enter your billing address building or unit identifier",
        showWhen: (formValues) => !formValues.sameAsBilling,
        group: "Billing Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-green-500",
        groupOrder: 1,
      },
      {
        name: "billingStreetAddress",
        label: " Street Address",
        type: "text",
        required: false,
        placeholder: "e.g. 123 Main Street",
        helperText: "Enter the full street address where you receive mail",
        showWhen: (formValues) => !formValues.sameAsBilling,
        group: "Billing Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-green-500",
        groupOrder: 2,
      },
      {
        name: "billingPremiseName",
        label: " Premise",
        type: "multi-select",
        maxSelection: 1,
        closeOnSelect: true,
        required: false,
        searchable: true,
        placeholder: "Select billing premise",
        options: premiseOptions,
        helperText: "Select the premise for billing address",
        showWhen: (formValues) => !formValues.sameAsBilling,
        group: "Billing Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          select:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-green-500",
        groupOrder: 3,
      },
      {
        name: "billingCity",
        label: " City",
        type: "text",
        required: false,
        placeholder: "City will be auto-filled",
        helperText: "Auto-populated based on billing premise selection",
        showWhen: (formValues) => !formValues.sameAsBilling,
        disabled: true,
        group: "Billing Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-green-500",
        groupOrder: 4,
      },
      {
        name: "billingArea",
        label: " Area",
        type: "text",
        required: false,
        placeholder: "Area will be auto-filled",
        helperText: "Auto-populated based on billing premise selection",
        showWhen: (formValues) => !formValues.sameAsBilling,
        disabled: true,
        group: "Billing Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-green-500",
        groupOrder: 5,
      },
      {
        name: "billingSubArea",
        label: " Sub Area",
        type: "text",
        required: false,
        placeholder: "Sub area will be auto-filled",
        helperText: "Auto-populated based on billing premise selection",
        showWhen: (formValues) => !formValues.sameAsBilling,
        disabled: true,
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-gray-50",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        group: "Billing Address",
        groupColor: "border-l-green-500",
        groupOrder: 6,
      },
      {
        name: "billingZipCode",
        label: " Zip/Postal Code",
        type: "text",
        required: false,
        placeholder: "e.g. 12345",
        // pattern: "^[0-9]{5}(-[0-9]{4})?$",
        helperText: "Enter the  postal code for your billing address",
        showWhen: (formValues) => !formValues.sameAsBilling,
        group: "Billing Address",
        classes: {
          container: "mb-4",
          label: "block text-sm font-medium text-gray-700 mb-2",
          input:
            "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-1",
        },
        groupColor: "border-l-green-500",
        groupOrder: 7,
      },
    ],
    [premiseOptions, isLoadingPremises]
  );

  // Create form instance - stable reference without dependencies
  const addressForm = FormService.createForm<AddressData>({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      houseNumber: "",
      streetAddress: "",
      city: "",
      area: "",
      subArea: "",
      premiseName: "",
      premiseNameDisplay: "",
      zipCode: "",
      sameAsBilling: true,
      billingHouseNumber: "",
      billingStreetAddress: "",
      billingCity: "",
      billingArea: "",
      billingSubArea: "",
      billingPremiseName: "",
      billingPremiseNameDisplay: "",
      billingZipCode: "",
      serviceAvailable: null,
    },
  });

  // Watch form values for reactive updates
  const watchedFormValues = addressForm.watch();

  // Load saved data on mount - use same pattern as ConsumerDetailsStep
  useEffect(() => {
    console.log(
      "🏢 AddressDetailsStep: Loading data for step:",
      currentStepIndex
    );
    console.log("🏢 AddressDetailsStep: Storage key:", storageKey);

    const loadData = () => {
      let savedStepData = null;

      // Try StepHelpers first
      if (stepHelpers) {
        savedStepData = stepHelpers.getStepData(currentStepIndex);
        console.log("🏢 StepHelpers data address:", savedStepData);
      }

      // Fallback to localStorage if StepHelpers is empty
      if (!savedStepData || Object.keys(savedStepData).length === 0) {
        try {
          console.log("🏢 Trying localStorage with key:", storageKey);
          const localStorageData = localStorage.getItem(storageKey);
          console.log("🏢 Raw localStorage data exists:", !!localStorageData);

          if (localStorageData) {
            const parsed = JSON.parse(localStorageData);
            console.log("🏢 Parsed localStorage keys:", Object.keys(parsed));
            console.log(
              "🏢 Looking for step data key:",
              currentStepIndex.toString()
            );
            console.log(
              "🏢 Available stepData keys:",
              Object.keys(parsed.stepData || {})
            );

            savedStepData = parsed.stepData?.[currentStepIndex.toString()];
            console.log("🏢 Found step data:", savedStepData);

            // If still no data, log the full structure for debugging
            if (!savedStepData) {
              console.log("🔍 Full stepData structure:", parsed.stepData);
            }
          }
        } catch (error) {
          console.error("❌ localStorage error:", error);
        }
      }

      // Populate form if we have data
      if (savedStepData && Object.keys(savedStepData).length > 0) {
        console.log("✅ Populating address form with:", savedStepData);
        const { premiseName, billingPremiseName, ...rest } = savedStepData;
        FormService.populateForm(addressForm, rest, formFields);
        if (savedStepData.serviceAvailable !== undefined) {
          setServiceAvailable(savedStepData.serviceAvailable);
        }

        // Also save this data to stepHelpers for future use
        if (stepHelpers) {
          stepHelpers.setStepData(currentStepIndex, savedStepData);
          console.log(
            "🏢 Data loaded from localStorage and saved to stepHelpers"
          );
        }

        console.log("✅ Address form populated successfully");
      } else {
        console.log(
          "ℹ️ No saved address data found for step:",
          currentStepIndex
        );
      }

      setIsLoading(false);
    };

    // Try immediately, but if no data, retry after a short delay
    loadData();

    // Retry mechanism in case of timing issues
    const retryTimer = setTimeout(() => {
      console.log("🔄 Retrying address data load...");
      loadData();
    }, 200);

    return () => clearTimeout(retryTimer);
  }, []); // Empty dependency array - load once on mount, never again

  const savedStepData = stepHelpers.getStepData(currentStepIndex);

  useEffect(() => {
    if (savedStepData?.premiseName?.[0] != null) {
      console.log(
        "debug populate premise",
        savedStepData,
        savedStepData?.premiseName
      );

      if (savedStepData.premiseName != null) {
        addressForm.setValue("premiseName", savedStepData.premiseName);
      }
      if (savedStepData.billingPremiseName != null) {
        addressForm.setValue(
          "billingPremiseName",
          savedStepData.billingPremiseName
        );
      }
    }
  }, [premisesData, savedStepData]);
  // Handle field changes with debouncing and stable dependencies
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // console.log(`🔧 Field change: ${fieldName} = ${value}`);

      // Handle premise selection - auto-populate city, area, subArea
      if (fieldName === "premiseName" && value) {
        console.log(`🏢 Premise selected: ${value}`);
        const premise = getPremiseByCode(value);
        console.log(`🏢 Premise data:`, premise);
        if (premise?.hierarchyNames) {
          // Batch all updates together using a single batch update
          addressForm.setValue("city", premise.hierarchyNames.state || "");
          addressForm.setValue("area", premise.hierarchyNames.area || "");
          addressForm.setValue("subArea", premise.hierarchyNames.subArea || "");
          addressForm.setValue("premiseNameDisplay", premise.name || "");
          console.log(`🏢 Auto-populated address fields:`, {
            city: premise.hierarchyNames.state,
            area: premise.hierarchyNames.area,
            subArea: premise.hierarchyNames.subArea,
            premiseName: premise.name,
          });

          // Force form to re-render by triggering a change
          setTimeout(() => {
            const currentValues = addressForm.getValues();
            console.log(
              `🏢 Current form values after setValue:`,
              currentValues
            );
            // Trigger re-render by calling trigger on the fields
            addressForm.trigger(["city", "area", "subArea"]);

            // Check if the watched values are updated
            const watchedValues = addressForm.watch();
            console.log(`🏢 Watched form values:`, watchedValues);
          }, 100);
        }
      }

      // Handle billing premise selection
      if (fieldName === "billingPremiseName" && value) {
        console.log(`🏢 Billing premise selected: ${value}`);
        const premise = getPremiseByCode(value);
        if (premise?.hierarchyNames) {
          addressForm.setValue(
            "billingCity",
            premise.hierarchyNames.state || ""
          );
          addressForm.setValue(
            "billingArea",
            premise.hierarchyNames.area || ""
          );
          addressForm.setValue(
            "billingSubArea",
            premise.hierarchyNames.subArea || ""
          );
          addressForm.setValue("billingPremiseNameDisplay", premise.name || "");
          console.log(`🏢 Auto-populated billing address fields`);
        }
      }

      // Handle "same as billing" checkbox
      if (fieldName === "sameAsBilling" && value) {
        console.log(`✅ Same as billing checked`);
        const currentValues = watchedFormValues;
        if (currentValues) {
          addressForm.setValue(
            "billingHouseNumber",
            currentValues.houseNumber || ""
          );
          addressForm.setValue(
            "billingStreetAddress",
            currentValues.streetAddress || ""
          );
          addressForm.setValue("billingCity", currentValues.city || "");
          addressForm.setValue("billingArea", currentValues.area || "");
          addressForm.setValue("billingSubArea", currentValues.subArea || "");
          addressForm.setValue(
            "billingPremiseName",
            currentValues.premiseName || ""
          );
          addressForm.setValue("billingZipCode", currentValues.zipCode || "");
          console.log(`✅ Copied service address to billing address`);
        }
      }

      // Check service availability when relevant fields change
      if (
        fieldName === "zipCode" ||
        fieldName === "city" ||
        fieldName === "area"
      ) {
        const currentValues = watchedFormValues;
        if (
          currentValues?.city &&
          currentValues?.area &&
          currentValues?.zipCode
        ) {
          console.log(`🔍 Checking service availability...`);
          checkServiceAvailability(
            currentValues.city,
            currentValues.area,
            currentValues.zipCode
          );
        }
      }
    },
    [getPremiseByCode, checkServiceAvailability, watchedFormValues] // Added watchedFormValues to dependencies
  );

  // const normalizePremise = (premise: string | string[] | undefined) => {
  //   if (Array.isArray(premise)) {
  //     return premise[0] ?? ""; // take 0th element if array
  //   }
  //   return premise ?? ""; // if already string
  // };

  const normalizePremise = (premise: string | string[] | undefined) => {
    if (Array.isArray(premise)) {
      return premise[0] ?? ""; // take first element if array
    }
    return premise ?? "";
  };

  // const handleFormSubmit = useCallback(
  //   async (data: AddressData) => {
  //     try {
  //       const isValid = await FormService.validateForm(addressForm);

  //       // ✅ Perform form validation
  //       if (!isValid) {

  //          console.warn(
  //           "❌ Data:",
  //          data
  //         );

  //         console.warn(
  //           "❌ Form validation failed. Errors:",
  //           addressForm.formState.errors
  //         );
  //         return;
  //       }

  //       // ✅ Copy billing data if sameAsBilling is true
  //       if (data.sameAsBilling) {
  //         data.billingHouseNumber = data.houseNumber;
  //         data.billingStreetAddress = data.streetAddress;
  //         data.billingCity = data.city;
  //         data.billingArea = data.area;
  //         data.billingSubArea = data.subArea;
  //         data.billingPremiseName = normalizePremise(data.premiseName);
  //         data.billingZipCode = data.zipCode;
  //         data.billingPremiseNameDisplay = data.premiseNameDisplay;
  //       }

  //       // ✅ Save step data
  //       stepHelpers?.setStepData(currentStepIndex, {
  //         ...data,
  //         serviceAvailable,
  //         timestamp: new Date().toISOString(),
  //       });

  //       clearValidationError?.();

  //       // ✅ Construct and submit payload
  //       const payload = {
  //         billingAddressData: {
  //           unit: data.billingHouseNumber,
  //           address: data.billingStreetAddress,
  //           city: data.billingCity,
  //           area: data.billingArea,
  //           subArea: data.billingSubArea,
  //           premise: normalizePremise(data.billingPremiseName),
  //           zipcode: data.billingZipCode,
  //         },
  //         serviceAddressData: {
  //           unit: data.houseNumber,
  //           address: data.streetAddress,
  //           city: data.city,
  //           area: data.area,
  //           subArea: data.subArea,
  //           premise: normalizePremise(data.premiseName),
  //           zipcode: data.zipCode,
  //         },
  //       };
  //       console.log(
  //         "debug payload",
  //         typeof payload?.billingAddressData?.premise,
  //         payload
  //       );
  //       await editConsumerMutation.mutateAsync({
  //         type: (currentStepIndex + 1).toString(),
  //         consumerId,
  //         payload,
  //       });
  //       logTimeUntilStep("Address Details Step Complete");
  //       onNext?.();
  //     } catch (error) {
  //       console.error("Error submitting address form:", error);
  //     }
  //   },
  //   [
  //     stepHelpers,
  //     currentStepIndex,
  //     serviceAvailable,
  //     clearValidationError,
  //     editConsumerMutation,
  //     consumerId,
  //     onNext,
  //   ]
  // );

  const handleFormSubmit = useCallback(
    async (data: AddressData) => {
      try {
        const isValid = await FormService.validateForm(addressForm);
        if (!isValid) {
          console.warn("❌ Data:", data);
          console.warn(
            "❌ Form validation failed. Errors:",
            addressForm.formState.errors
          );
          return;
        }

        // 🚨 Extra validation: if not sameAsBilling, check billing fields
        if (!data.sameAsBilling) {
          if (
            !data.billingHouseNumber ||
            !data.billingStreetAddress ||
            !data.billingPremiseName ||
            (Array.isArray(data.billingPremiseName) &&
              data.billingPremiseName.length === 0)
          ) {
            console.warn("data", data);
            toast({
              title: "Missing Billing Details",
              description: "Please fill in all billing address fields.",
              variant: "destructive",
            });
            return;
          }
        }

        // ✅ Copy billing data if sameAsBilling is true
        if (data.sameAsBilling) {
          data.billingHouseNumber = data.houseNumber;
          data.billingStreetAddress = data.streetAddress;
          data.billingCity = data.city;
          data.billingArea = data.area;
          data.billingSubArea = data.subArea;
          // keep as array in form
          data.billingPremiseName = Array.isArray(data.premiseName)
            ? data.premiseName
            : [data.premiseName].filter(Boolean);
          data.billingZipCode = data.zipCode;
          data.billingPremiseNameDisplay = data.premiseNameDisplay;
        }

        // ✅ Save step data (still keep array for form state)
        stepHelpers?.setStepData(currentStepIndex, {
          ...data,
          serviceAvailable,
          timestamp: new Date().toISOString(),
        });

        clearValidationError?.();

        // ✅ Construct payload (convert to string here)
        const payload = {
          billingAddressData: {
            unit: data.billingHouseNumber,
            address: data.billingStreetAddress,
            city: data.billingCity,
            area: data.billingArea,
            subArea: data.billingSubArea,
            premise: normalizePremise(data.billingPremiseName), // 👈 converts to string
            zipcode: data.billingZipCode,
          },
          serviceAddressData: {
            unit: data.houseNumber,
            address: data.streetAddress,
            city: data.city,
            area: data.area,
            subArea: data.subArea,
            premise: normalizePremise(data.premiseName), // 👈 converts to string
            zipcode: data.zipCode,
          },
        };

        console.log(
          "debug payload",
          typeof payload?.billingAddressData?.premise,
          payload
        );

        await editConsumerMutation.mutateAsync({
          type: (currentStepIndex + 1).toString(),
          consumerId,
          payload,
        });

        logTimeUntilStep("Address Details Step Complete");
        onNext?.();
      } catch (error) {
        console.error("Error submitting address form:", error);
      }
    },
    [
      stepHelpers,
      currentStepIndex,
      serviceAvailable,
      clearValidationError,
      editConsumerMutation,
      consumerId,
      onNext,
    ]
  );

  // Memoized address preview with safe property access
  const serviceAddressPreview = useMemo(() => {
    const formData = watchedFormValues;
    if (
      !formData?.houseNumber &&
      !formData?.streetAddress &&
      !formData?.city &&
      !formData?.area
    ) {
      return "Please fill in service address details to see preview";
    }

    const selectedPremise = formData?.premiseName[0]
      ? getPremiseByCode(formData.premiseName)
      : null;
    const premiseText = selectedPremise ? ` (${selectedPremise.name})` : "";

    return (
      <div className="space-y-1">
        <p>
          <strong>{formData?.houseNumber || ""}</strong>{" "}
          {formData?.streetAddress || ""}
          {premiseText}
        </p>
        <p>
          {formData?.area && `${formData.area}, `}
          {formData?.city || ""}
        </p>
        <p>{formData?.zipCode || ""}</p>
      </div>
    );
  }, [watchedFormValues, getPremiseByCode]);

  const billingAddressPreview = useMemo(() => {
    const formData = watchedFormValues;
    if (formData?.sameAsBilling) {
      return serviceAddressPreview;
    }

    if (
      !formData?.billingHouseNumber &&
      !formData?.billingStreetAddress &&
      !formData?.billingCity &&
      !formData?.billingArea
    ) {
      return "Please fill in billing address details to see preview";
    }

    const selectedBillingPremise = formData?.billingPremiseName
      ? getPremiseByCode(formData.billingPremiseName)
      : null;
    const premiseText = selectedBillingPremise
      ? ` (${selectedBillingPremise.name})`
      : "";

    return (
      <div className="space-y-1">
        <p>
          <strong>{formData?.billingHouseNumber || ""}</strong>{" "}
          {formData?.billingStreetAddress || ""}
          {premiseText}
        </p>
        <p>
          {formData?.billingArea && `${formData.billingArea}, `}
          {formData?.billingCity || ""}
        </p>
        <p>{formData?.billingZipCode || ""}</p>
      </div>
    );
  }, [watchedFormValues, getPremiseByCode, serviceAddressPreview]);

  return (
    <div className="space-y-6">
      {/* Information Alert */}
      <Accordion type="single" collapsible className="mb-6">
        <AccordionItem
          value="important-info"
          className="border border-blue-200 rounded-lg bg-blue-50"
        >
          <AccordionTrigger className="px-4 py-3 hover:bg-blue-100 rounded-t-lg">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-500 mr-3 flex-shrink-0" />
              <span className="font-medium text-blue-800">
                Address Information
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <ul className="text-blue-700 text-sm space-y-1 list-disc pl-5">
              <li>
                Select a premise to automatically populate city, area, and
                sub-area
              </li>
              <li>
                The billing address can be different or the same as the service
                address
              </li>
              <li>We'll verify if service is available at your location</li>
              <li>All fields marked with an asterisk (*) are required</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Dynamic Form */}
      <DynamicFormV2
        fields={formFields}
        form={addressForm}
        onSubmit={handleFormSubmit}
        onFieldChange={handleFieldChange}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          showProgress: false,
        }}
        gridColumns={3}
        responsiveColumns={{ sm: 1, md: 3 }}
      />

      {/* Address Preview Section */}
      <div className="mt-8 space-y-6">
        <h3 className="text-lg font-semibold text-gray-800">Address Preview</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Service Address Preview */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-3">
              <Home className="h-5 w-5 text-blue-600" />
              <h4 className="font-medium text-blue-800">Service Address</h4>
            </div>
            <div className="text-sm text-blue-700 mb-3">
              How your service address will appear:
            </div>
            <div className="font-medium text-gray-900">
              {serviceAddressPreview}
            </div>
          </div>

          {/* Billing Address Preview */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center gap-2 mb-3">
              <Building className="h-5 w-5 text-green-600" />
              <h4 className="font-medium text-green-800">Billing Address</h4>
            </div>
            <div className="text-sm text-green-700 mb-3">
              How it will appear on your bill:
            </div>
            <div className="font-medium text-gray-900">
              {billingAddressPreview}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <div className="flex items-center space-x-3">
          <Button
            onClick={onPrevious}
            variant="outline"
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft size={16} />
            <span>Previous</span>
          </Button>
        </div>

        <Button
          onClick={() => handleFormSubmit(watchedFormValues)}
          disabled={isValidating}
          className="flex items-center space-x-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <span>Save and Continue</span>
          <ChevronRight size={16} />
        </Button>
      </div>
    </div>
  );
}
